import { defineComponent, h, useAttrs } from 'vue'
import {
  VCheckbox,
  VRadio,
  VRadioGroup,
  VSelectionControl,
  VSelectionControlGroup,
  VSwitch
} from 'vuetify/components'
import { hasBooleanishAttr } from './legacyInputProps'

if (VSelectionControl) {
  VSelectionControl.compatConfig = {
    ...(VSelectionControl.compatConfig || {}),
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  }
}

if (VSelectionControlGroup) {
  VSelectionControlGroup.compatConfig = {
    ...(VSelectionControlGroup.compatConfig || {}),
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  }
}

export function normalizeControlAttrs(
  attrs,
  { defaultModelFromValue = true, preserveValue = true } = {}
) {
  const normalized = { ...attrs }
  const legacyInputValue = attrs['input-value'] ?? attrs.inputValue

  if (attrs['input-value'] !== undefined || attrs.inputValue !== undefined) {
    normalized.modelValue = legacyInputValue

    if (typeof legacyInputValue === 'boolean') {
      normalized.trueValue = attrs.trueValue ?? attrs['true-value'] ?? true
      normalized.falseValue = attrs.falseValue ?? attrs['false-value'] ?? false
      normalized.checked = legacyInputValue
    }
  } else if (defaultModelFromValue && normalized.modelValue == null) {
    normalized.modelValue = attrs.value
  }

  if (hasBooleanishAttr(attrs.dense) && normalized.density == null) {
    normalized.density = 'compact'
  }

  delete normalized['input-value']
  delete normalized.inputValue
  delete normalized['true-value']
  delete normalized['false-value']
  if (!preserveValue) delete normalized.value
  delete normalized.dense

  return normalized
}

function createSelectionControlCompat(
  name,
  Component,
  legacyClasses = [],
  options = {}
) {
  Component.compatConfig = {
    ...(Component.compatConfig || {}),
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false
  }

  return defineComponent({
    name,
    compatConfig: {
      RENDER_FUNCTION: false,
      COMPONENT_V_MODEL: false,
      INSTANCE_ATTRS_CLASS_STYLE: false
    },
    inheritAttrs: false,
    emits: ['update:modelValue', 'input', 'change'],

    setup(_, { emit, slots }) {
      const attrs = useAttrs()

      return () => {
        const normalizedAttrs = options.normalizeAttrs
          ? options.normalizeAttrs(attrs)
          : normalizeControlAttrs(attrs, options)

        delete normalizedAttrs.onInput
        delete normalizedAttrs.onChange
        delete normalizedAttrs['onUpdate:modelValue']

        return h(
          Component,
          {
            ...normalizedAttrs,
            class: [legacyClasses, attrs.class],
            'onUpdate:modelValue': (value) => {
              emit('update:modelValue', value)
              emit('input', value)
              emit('change', value)
            }
          },
          slots
        )
      }
    }
  })
}

export function normalizeRadioAttrs(attrs) {
  const normalized = normalizeControlAttrs(attrs, {
    defaultModelFromValue: false
  })

  if (attrs['input-value'] === undefined && attrs.inputValue === undefined) {
    delete normalized.modelValue
  }

  return normalized
}

const VRadioCompat = createSelectionControlCompat(
  'VRadioCompat',
  VRadio,
  ['v-input--selection-controls'],
  { normalizeAttrs: normalizeRadioAttrs }
)

const VCheckboxCompat = createSelectionControlCompat(
  'VCheckboxCompat',
  VCheckbox,
  ['v-input--selection-controls']
)

const VSwitchCompat = createSelectionControlCompat('VSwitchCompat', VSwitch, [
  'v-input--selection-controls'
])

const VRadioGroupCompat = createSelectionControlCompat(
  'VRadioGroupCompat',
  VRadioGroup,
  [],
  { preserveValue: false }
)

export { VCheckboxCompat, VRadioCompat, VRadioGroupCompat, VSwitchCompat }
